/**
 * Hook to determine user's permission level for routing features
 * Returns permission flags for assignment, status updates, and read-only state
 *
 * Permission Logic:
 * - Service Advisors+ have `recalls:assign_technicians` = full access
 * - Technicians don't have that permission = can only update their own routes
 * - Linking technician to user is done by email match
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { RouteTrackerItem } from '@/types/routeTracking';

export interface RoutingPermissions {
  /** Can assign/edit technician assignments on /routing/manual */
  canAssignTechnicians: boolean;
  /** Can update ANY recall status (service advisor+) */
  canUpdateAnyRecallStatus: boolean;
  /** Can update OWN assigned recalls only (technician) */
  canUpdateOwnRecallStatus: boolean;
  /** True if user cannot assign technicians (read-only for assignments) */
  isReadOnlyForAssignments: boolean;
  /** User's linked technician ID (by email match), null if not linked */
  linkedTechnicianId: string | null;
  /** True while looking up technician by email */
  isLoading: boolean;
  /** Helper function to check if user can edit a specific route */
  canEditRoute: (route: RouteTrackerItem) => boolean;
}

// eslint-disable-next-line max-lines-per-function
export function useRoutingPermissions(): RoutingPermissions {
  const { hasPermission, user } = useAuth();
  const tenantId = useCurrentTenantId();
  const [linkedTechnicianId, setLinkedTechnicianId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const canAssignTechnicians = hasPermission({
    resource: 'recalls',
    action: 'assign_technicians'
  });

  // Service advisors+ can update any recall
  // Technicians can only update their own
  const canUpdateAnyRecallStatus = canAssignTechnicians;
  const canUpdateOwnRecallStatus = !canAssignTechnicians;

  // Look up technician ID by matching user email
  useEffect(() => {
    // Only look up if user is a technician (doesn't have assign permission)
    if (!canUpdateOwnRecallStatus || !user?.email || !tenantId) {
      setLinkedTechnicianId(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const lookupTechnician = async () => {
      try {
        const prodClient = getProdSupabaseClient();
        const userEmail = user.email as string; // Already checked above
        const { data, error } = await prodClient
          .from('technicians')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('email', userEmail)
          .eq('is_active', true)
          .single();

        if (isMounted) {
          if (error && error.code !== 'PGRST116') {
            // PGRST116 = no rows returned (not an error for our use case)
            // eslint-disable-next-line no-console
            console.warn('Error looking up technician:', error);
          }
          const techId = (data as { id: string } | null)?.id ?? null;
          setLinkedTechnicianId(techId);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          // eslint-disable-next-line no-console
          console.warn('Failed to lookup technician:', err);
          setLinkedTechnicianId(null);
          setIsLoading(false);
        }
      }
    };

    lookupTechnician();

    return () => {
      isMounted = false;
    };
  }, [user?.email, tenantId, canUpdateOwnRecallStatus]);

  // Helper function to check if user can edit a specific route
  const canEditRoute = useCallback(
    (route: RouteTrackerItem): boolean => {
      // Service advisors+ can edit any route
      if (canUpdateAnyRecallStatus) return true;

      // Technicians can only edit routes assigned to them
      if (canUpdateOwnRecallStatus && linkedTechnicianId) {
        return route.technicianId === linkedTechnicianId;
      }

      return false;
    },
    [canUpdateAnyRecallStatus, canUpdateOwnRecallStatus, linkedTechnicianId]
  );

  return {
    canAssignTechnicians,
    canUpdateAnyRecallStatus,
    canUpdateOwnRecallStatus,
    isReadOnlyForAssignments: !canAssignTechnicians,
    linkedTechnicianId,
    isLoading,
    canEditRoute
  };
}
